// Module: brick-registry
// Role: Maps checkpoint kind + artefact_requirements to brick components.
//       FlowScreen dispatches to bricks via this registry.
// Uses: VideoUploadBrick, ScreenshotBrick, UrlInputBrick
// Used by: FlowScreen.tsx

import type { ComponentType } from 'react';
import type { Checkpoint, ArtefactRequirement } from '../../../api/flow-types';
import type { VideoUploadBrickProps } from './VideoUploadBrick';
import { VideoUploadBrick } from './VideoUploadBrick';
import { ScreenshotBrick } from './ScreenshotBrick';
import { UrlInputBrick } from './UrlInputBrick';
import { MultiImageUploadBrick } from './MultiImageUploadBrick';

// Common brick props — all bricks share the same base prop shape.
export type BrickProps = VideoUploadBrickProps;

// ArtefactKind -> brick component mapping.
const BRICK_REGISTRY: Record<string, ComponentType<BrickProps>> = {
  video: VideoUploadBrick as ComponentType<BrickProps>,
  screenshot: ScreenshotBrick as ComponentType<BrickProps>,
  url: UrlInputBrick as ComponentType<BrickProps>,
};
const MULTI_IMAGE_KINDS = new Set(['screenshot', 'real_photo']);

export interface BrickEntry {
  /** Stable React key: `${checkpoint.id}-${kind}-${index}` (duplicate-kind + step-state safe). */
  key: string;
  Component: ComponentType<BrickProps>;
  requirement: ArtefactRequirement;
}

/**
 * Resolve the bricks needed for a checkpoint, keyed by (kind,index)
 * to handle duplicate-kind requirements (EC-10).
 */
export function bricksForCheckpoint(checkpoint: Checkpoint): BrickEntry[] {
  const reqs = checkpoint.artefact_requirements;
  if (!reqs || reqs.length === 0) return [];

  // BUG-1589: UrlInputBrick already accepts the url plus its screenshot, so a
  // mixed requirement pair must resolve to that composite whichever order the
  // requirements were declared in. Taking entries[0] must never drop the URL.
  const urlReq = reqs.find((req) => req.kind === 'url');
  const hasScreenshotReq = reqs.some((req) => req.kind === 'screenshot');
  if (urlReq && hasScreenshotReq) {
    return [
      {
        key: `${checkpoint.id}-url-composite`,
        Component: UrlInputBrick as ComponentType<BrickProps>,
        requirement: urlReq,
      },
    ];
  }

  const imageReqs = reqs.filter((req) => MULTI_IMAGE_KINDS.has(req.kind));
  if (imageReqs.length > 1) {
    return [
      {
        key: `${checkpoint.id}-multi-image`,
        Component: MultiImageUploadBrick as ComponentType<BrickProps>,
        requirement: imageReqs[0],
      },
    ];
  }

  const entries: BrickEntry[] = [];
  const kindCounters: Record<string, number> = {};

  for (const req of reqs) {
    const kind = req.kind;
    const idx = kindCounters[kind] ?? 0;
    kindCounters[kind] = idx + 1;

    const Component = BRICK_REGISTRY[kind];
    if (!Component) continue; // unknown kind — skip (InstructionScreen fallback)

    entries.push({
      key: `${checkpoint.id}-${kind}-${idx}`,
      Component,
      requirement: req,
    });
  }

  return entries;
}
